// Cache the current version to avoid unnecessary refreshes
let currentVersion: string | null = localStorage.getItem('app_version');

/**
 * Checks if a new version of the application is available
 * @returns true if update is available and page should refresh
 */
export const checkForUpdates = async (): Promise<boolean> => {
  try {
    // Add cache-busting query parameter
    const timestamp = new Date().getTime();
    const response = await fetch(`/version.json?_=${timestamp}`);

    // Handle missing file
    if (!response.ok) {
      console.log('Version file not found, skipping update check');
      return false;
    }

    // Check content type to avoid parsing HTML
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.log('Received non-JSON response, skipping update check');
      return false;
    }

    // Safely parse the response as text first
    try {
      const text = await response.text();
      // Quick check to see if we got HTML instead of JSON
      if (
        text.trim().toLowerCase().startsWith('<!doctype') ||
        text.trim().toLowerCase().startsWith('<html')
      ) {
        console.log('Received HTML instead of JSON, skipping update check');
        return false;
      }

      const data = JSON.parse(text);

      if (!data.version) {
        console.log('Version data missing version field, skipping update check');
        return false;
      }

      const nextVersion = String(data.version);

      if (!currentVersion) {
        console.log(`Initializing app version: ${nextVersion}`);
        localStorage.setItem('app_version', nextVersion);
        currentVersion = nextVersion;
        return false;
      }

      // If the stored version is different, update is needed
      if (currentVersion !== nextVersion) {
        console.log(`Update available: ${currentVersion} → ${nextVersion}`);

        // Store the new version
        localStorage.setItem('app_version', nextVersion);
        currentVersion = nextVersion;

        return true; // Update needed
      }

      return false; // No update needed
    } catch (parseError) {
      console.error('Error parsing version data:', parseError);
      return false;
    }
  } catch (error) {
    console.error('Failed to check for updates:', error);
    return false;
  }
};

/**
 * Apply update by refreshing the page
 */
export const applyUpdate = (): void => {
  console.log('Applying update - refreshing page');
  window.location.reload();
};
